import { useState } from 'react';
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { Promotion } from '../types/promotion';
import { getPromotionImageUrl } from '../api/promotionService';
import MarkdownRenderer from './MarkdownRenderer';
import trackEvent, { AnalyticsEvent } from '../utils/trackEvent';

interface PromotionModalProps {
  open: boolean;
  onClose: () => void;
  promotion: Promotion;
}

export default function PromotionModal({ open, onClose, promotion }: PromotionModalProps) {
  const [copySnackbar, setCopySnackbar] = useState(false);
  const imageUrl = getPromotionImageUrl(promotion);

  const handleCopy = () => {
    void navigator.clipboard.writeText(promotion.code);
    setCopySnackbar(true);
    trackEvent(AnalyticsEvent.PROMOTION_CODE_COPY, {
      promotion_id: promotion.id,
      code: promotion.code,
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="promotion-modal-title"
      >
        <DialogTitle id="promotion-modal-title" sx={{ pr: 6 }}>
          Promotion Details
          <IconButton
            aria-label="Close promotion details"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {imageUrl && (
            <Box
              component="img"
              src={imageUrl}
              alt="Promotion"
              sx={{ width: '100%', borderRadius: 1, mb: 2 }}
            />
          )}
          {promotion.long_description && <MarkdownRenderer content={promotion.long_description} />}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Chip
              label={promotion.code}
              icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              onClick={handleCopy}
              aria-label={`Copy promotion code ${promotion.code}`}
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                fontWeight: 700,
                fontSize: '1rem',
                py: 2,
                cursor: 'pointer',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => {
          setCopySnackbar(false);
        }}
        message="Copied!"
        aria-live="polite"
      />
    </>
  );
}
